/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { simpleFetch } from '../../restWrapper';
import './ReceiptVoidDialog.css';

export default function ReceiptVoidDialog({ payment, text, onSubmit, onCancel, busy, error }) {
  const [reason, setReason] = useState('');
  const [notifyDonor, setNotifyDonor] = useState(false);
  const [message, setMessage] = useState(null);
  const [previewError, setPreviewError] = useState(false);
  const [retry, setRetry] = useState(0);
  const reasonRef = useRef(null);
  useEffect(() => {
    const previous = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    reasonRef.current.focus();
    return () => { document.body.style.overflow = previousOverflow; if (previous && previous.focus) previous.focus(); };
  }, []);
  useEffect(() => {
    let cancelled = false;
    if (notifyDonor && !message) {
      setPreviewError(false);
      simpleFetch(`/organizations/payments/${payment.id}/void-receipt-preview`, 'GET').then(response => {
        if (cancelled) return;
        if (response.status === 200 && response.body.from && response.body.to && response.body.subject && response.body.body) setMessage(response.body);
        else setPreviewError(true);
      }).catch(() => { if (!cancelled) setPreviewError(true); });
    }
    return () => { cancelled = true; };
  }, [payment.id, notifyDonor, message, retry]);
  const ready = !!reason.trim() && (!notifyDonor || (message && message.subject.trim() && message.body.trim() && !/[\r\n]/.test(message.subject)));
  return (
    <div className="ReceiptVoid-backdrop">
      <form className="ReceiptVoid-dialog" role="dialog" aria-modal="true" aria-labelledby="receipt-void-title" aria-describedby="receipt-void-warning"
        onKeyDown={event => {
          if (event.key === 'Escape' && !busy) onCancel();
          if (event.key === 'Tab') {
            const controls = [...event.currentTarget.querySelectorAll('textarea, input, button')].filter(el => !el.disabled);
            const first = controls[0], last = controls[controls.length - 1];
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
          }
        }}
        onSubmit={event => { event.preventDefault(); if (ready && !busy) onSubmit(reason.trim(), notifyDonor, notifyDonor ? message : undefined); }}>
        <header className="ReceiptVoid-header">
          <span className="ReceiptVoid-headingIcon" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 3h9l3 3v15l-3-2-3 2-3-2-3 2V3Z" /><path d="M9 8h6M9 12h6M9 16h3" />
            </svg>
          </span>
          <div><h2 id="receipt-void-title">{text.title}</h2><p>{text.dialogIntro}</p></div>
          <button className="ReceiptVoid-close" type="button" aria-label={text.close} onClick={onCancel} disabled={busy}>
            <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="m6 6 12 12M18 6 6 18" /></svg>
          </button>
        </header>
        <div className="ReceiptVoid-content">
          <dl className="ReceiptVoid-context">
            <div><dt>{text.receipt}</dt><dd>{payment.organizationReceiptId}</dd></div>
            <div><dt>{text.transaction}</dt><dd>#{payment.id}</dd></div>
          </dl>
          <div className="ReceiptVoid-notice">
            <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v6m0 4h.01" /></svg>
            <p id="receipt-void-warning">{text.warning}</p>
          </div>
          <div className="ReceiptVoid-field">
            <div className="ReceiptVoid-fieldHeading"><label htmlFor="receipt-void-reason">{text.reason}</label><span aria-hidden="true">{reason.length}/1000</span></div>
            <textarea ref={reasonRef} id="receipt-void-reason" aria-describedby="receipt-void-reason-help" required maxLength={1000} value={reason} disabled={busy}
              placeholder={text.reasonPlaceholder} onChange={event => setReason(event.target.value)} />
            <small id="receipt-void-reason-help">{text.internalReason}</small>
          </div>
          <label className={`ReceiptVoid-notify${notifyDonor ? ' ReceiptVoid-notify--selected' : ''}`}>
            <span className="ReceiptVoid-notifyIcon" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="3" /><path d="m4 7 8 6 8-6" /></svg></span>
            <span className="ReceiptVoid-notifyCopy"><strong>{text.notify}</strong><span>{text.notifyHelp}</span></span>
            <input type="checkbox" aria-label={text.notify} checked={notifyDonor} disabled={busy}
              onChange={event => setNotifyDonor(event.target.checked)} />
          </label>
          {notifyDonor && <section className="ReceiptVoid-email" aria-label={text.emailPreview}>
            <div className="ReceiptVoid-emailHeading"><h3>{text.emailPreview}</h3><span>{text.editablePreview}</span></div><p>{text.emailHelp}</p>
            {!message && !previewError && <p role="status">{text.previewLoading}</p>}
            {previewError && <p role="alert">{text.previewFailed} <button type="button" onClick={() => setRetry(n => n + 1)}>{text.previewRetry}</button></p>}
            {message && <>
              <dl><dt>{text.emailFrom}</dt><dd>{message.from}</dd><dt>{text.emailTo}</dt><dd>{message.to}</dd></dl>
              <label htmlFor="receipt-void-subject">{text.emailSubject}</label>
              <input id="receipt-void-subject" maxLength={200} required disabled={busy} value={message.subject}
                onChange={event => setMessage({ ...message, subject: event.target.value })} />
              <label htmlFor="receipt-void-message">{text.emailMessage}</label>
              <textarea id="receipt-void-message" required maxLength={10000} disabled={busy} value={message.body}
                onChange={event => setMessage({ ...message, body: event.target.value })} />
              <small>{text.emailExact}</small>
            </>}
          </section>}
          {error && <p className="ReceiptVoid-error" role="alert">{error}</p>}
        </div>
        <footer className="ReceiptVoid-actions">
          <span className="ReceiptVoid-retainedHint"><svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="5" y="10" width="14" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg>{text.retainedHint}</span>
          <div className="ReceiptVoid-actionButtons">
            <button type="button" onClick={onCancel} disabled={busy}>{text.cancel}</button>
            <button type="submit" disabled={busy || !ready}>{busy && <span className="ReceiptVoid-spinner" aria-hidden="true" />}{busy ? text.working : text.title}</button>
          </div>
        </footer>
      </form>
    </div>
  );
}
ReceiptVoidDialog.propTypes = { payment: PropTypes.object.isRequired, text: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired, onCancel: PropTypes.func.isRequired, busy: PropTypes.bool, error: PropTypes.string };
